/**
 * Iter 98 — header notifications bell for vendor/admin/agent (spec §20).
 * Polls /api/ops/notifications; badge shows unread; opening the sheet marks
 * everything read. Items link straight to the booking.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Bell, CalendarCheck2, Wallet, KeyRound, AlarmClockOff, FileSignature, FileWarning, Camera } from "lucide-react";

const KIND_META = {
  new_reservation:      { icon: CalendarCheck2, cls: "bg-blue-50 text-blue-600 ring-blue-100" },
  payment_received:     { icon: Wallet,         cls: "bg-emerald-50 text-emerald-600 ring-emerald-100" },
  pickup_approaching:   { icon: KeyRound,       cls: "bg-amber-50 text-amber-600 ring-amber-100" },
  vehicle_overdue:      { icon: AlarmClockOff,  cls: "bg-rose-50 text-rose-600 ring-rose-100" },
  agreement_unsigned:   { icon: FileSignature,  cls: "bg-violet-50 text-violet-600 ring-violet-100" },
  document_expiring:    { icon: FileWarning,    cls: "bg-orange-50 text-orange-600 ring-orange-100" },
  cars_missing_photos:  { icon: Camera,         cls: "bg-sky-50 text-sky-600 ring-sky-100" },
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/ops/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleOpen = (o) => {
    setOpen(o);
    if (o && data?.unread > 0) {
      api.post("/ops/notifications/seen").then(() => setData((d) => (d ? { ...d, unread: 0 } : d))).catch(() => {});
    }
  };

  const unread = data?.unread || 0;

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="relative h-9 w-9 rounded-full border border-border bg-white flex items-center justify-center text-neutral-600 hover:border-neutral-400 transition"
          data-testid="notifications-bell"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="notifications-sheet">
        <SheetHeader className="mb-3">
          <SheetTitle className="text-left">Notifications</SheetTitle>
        </SheetHeader>
        <div className="space-y-2 pb-6">
          {(data?.items || []).length === 0 && (
            <div className="text-sm text-neutral-500 text-center py-10" data-testid="notifications-empty">All quiet — no alerts right now.</div>
          )}
          {(data?.items || []).map((n) => {
            const meta = KIND_META[n.kind] || KIND_META.new_reservation;
            const Icon = meta.icon;
            const isNew = !data?.seen_at || String(n.at || "") > data.seen_at || n.kind === "vehicle_overdue";
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => { setOpen(false); navigate(n.href || `/agreements/${n.booking_id}`); }}
                className={`w-full text-left rounded-2xl border p-3 flex items-start gap-3 hover:border-blue-300 transition ${isNew ? "border-blue-200 bg-blue-50/40" : "border-border bg-white"}`}
                data-testid={`notification-${n.id}`}
              >
                <span className={`h-9 w-9 rounded-xl ring-1 flex items-center justify-center shrink-0 ${meta.cls}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold truncate">{n.title}</span>
                  <span className="block text-xs text-neutral-500 truncate">{n.subtitle}</span>
                </span>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
